import { useState, useRef } from "react";
import { Download, FileText, Copy, Check, Printer } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

interface ExportControlsProps {
  reportData: any;
  reportRef?: React.RefObject<HTMLDivElement>;
}

// Generate markdown report from data
function generateMarkdownReport(data: any): string {
  const lines: string[] = [];
  
  lines.push("# Sigorta Hasar Analiz Raporu");
  lines.push("");
  lines.push(`**Rapor No:** ${data.reportId || "N/A"}`);
  lines.push(`**Tarih:** ${data.generatedAt ? new Date(data.generatedAt).toLocaleString("tr-TR") : new Date().toLocaleString("tr-TR")}`);
  lines.push("");
  
  // Fault Analysis
  lines.push("## 1. Kusur Analizi");
  if (data.faultAnalysis) {
    lines.push(`- **Kusur Oranı:** %${data.faultAnalysis.faultPercentage || 0}`);
    lines.push(`- **Değerlendirme:** ${data.faultAnalysis.determination || "N/A"}`);
    if (data.faultAnalysis.reasoning) {
      lines.push(`- **Gerekçe:** ${data.faultAnalysis.reasoning}`);
    }
    if (data.faultAnalysis.legalBasis) {
      lines.push(`- **Yasal Dayanak:** ${data.faultAnalysis.legalBasis}`);
    }
  }
  lines.push("");
  
  // Damage Assessment
  lines.push("## 2. Hasar Değerlendirmesi");
  if (data.damageAssessment) {
    lines.push(`- **Hasar Şiddeti:** ${data.damageAssessment.severity || "orta"}`);
    lines.push(`- **Onarılabilirlik:** ${data.damageAssessment.repairability || "onarılabilir"}`);
    if (data.damageAssessment.areas?.length > 0) {
      lines.push("- **Hasarlı Bölgeler:**");
      data.damageAssessment.areas.forEach((area: string) => {
        lines.push(`  - ${area}`);
      });
    }
    if (data.damageAssessment.description) {
      lines.push(`- **Detaylı Açıklama:** ${data.damageAssessment.description}`);
    }
  }
  lines.push("");
  
  // Cost Estimation
  lines.push("## 3. Maliyet Tahmini");
  if (data.costEstimation) {
    lines.push(`| Kalem | Tutar |`);
    lines.push(`|-------|-------|`);
    lines.push(`| İşçilik | ₺${(data.costEstimation.laborCost || 0).toLocaleString("tr-TR")} |`);
    lines.push(`| Parça | ₺${(data.costEstimation.partsCost || 0).toLocaleString("tr-TR")} |`);
    lines.push(`| Boya | ₺${(data.costEstimation.paintCost || 0).toLocaleString("tr-TR")} |`);
    lines.push(`| **TOPLAM** | **₺${(data.costEstimation.totalCost || 0).toLocaleString("tr-TR")}** |`);
  }
  lines.push("");
  
  // Recommendations
  if (data.recommendations?.length > 0) {
    lines.push("## 4. Öneriler ve Sonuç");
    data.recommendations.forEach((rec: string, i: number) => {
      lines.push(`${i + 1}. ${rec}`);
    });
    lines.push("");
  }
  
  lines.push("---");
  lines.push("");
  lines.push("*Bu rapor Insura AI sistemi tarafından otomatik olarak oluşturulmuştur.*");
  lines.push(`*Oluşturulma Tarihi: ${new Date().toLocaleString("tr-TR")}*`);
  
  return lines.join("\n");
}

// Download content as file
function downloadAsFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function ExportControls({ reportData, reportRef }: ExportControlsProps) {
  const [exporting, setExporting] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleExportPDF = async () => {
    setExporting("pdf");
    try {
      if (reportRef?.current) {
        // Use html2canvas to capture the report element
        const canvas = await html2canvas(reportRef.current, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
        });
        
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });
        
        const imgWidth = 210; // A4 width in mm
        const pageHeight = 297; // A4 height in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;
        
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
        
        // Add more pages if needed
        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }
        
        pdf.save(`insura-rapor-${reportData.reportId || Date.now()}.pdf`);
      } else {
        // Fallback: Generate PDF from data directly
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });
        
        // Add header
        pdf.setFontSize(20);
        pdf.setFont("helvetica", "bold");
        pdf.text("Sigorta Hasar Analiz Raporu", 105, 20, { align: "center" });
        
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");
        pdf.text(`Rapor No: ${reportData.reportId || "N/A"}`, 20, 35);
        pdf.text(`Tarih: ${new Date(reportData.generatedAt || Date.now()).toLocaleString("tr-TR")}`, 20, 42);
        
        let y = 55;
        
        // Fault Analysis Section
        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        pdf.text("1. Kusur Analizi", 20, y);
        y += 10;
        
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");
        if (reportData.faultAnalysis) {
          pdf.text(`Kusur Orani: %${reportData.faultAnalysis.faultPercentage || 0}`, 25, y);
          y += 7;
          
          const determination = reportData.faultAnalysis.determination || "N/A";
          const detLines = pdf.splitTextToSize(determination, 160);
          pdf.text(detLines, 25, y);
          y += detLines.length * 5 + 5;
        }
        
        // Damage Assessment Section
        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        pdf.text("2. Hasar Degerlendirmesi", 20, y);
        y += 10;
        
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");
        if (reportData.damageAssessment) {
          pdf.text(`Siddet: ${reportData.damageAssessment.severity || "orta"}`, 25, y);
          y += 7;
          pdf.text(`Onarilabilirlik: ${reportData.damageAssessment.repairability || "onarilabilir"}`, 25, y);
          y += 10;
        }
        
        // Cost Section
        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        pdf.text("3. Maliyet Tahmini", 20, y);
        y += 10;
        
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");
        if (reportData.costEstimation) {
          pdf.text(`Iscilik: ${(reportData.costEstimation.laborCost || 0).toLocaleString("tr-TR")} TL`, 25, y);
          y += 7;
          pdf.text(`Parca: ${(reportData.costEstimation.partsCost || 0).toLocaleString("tr-TR")} TL`, 25, y);
          y += 7;
          pdf.text(`Boya: ${(reportData.costEstimation.paintCost || 0).toLocaleString("tr-TR")} TL`, 25, y);
          y += 7;
          pdf.setFont("helvetica", "bold");
          pdf.text(`TOPLAM: ${(reportData.costEstimation.totalCost || 0).toLocaleString("tr-TR")} TL`, 25, y);
          y += 15;
        }
        
        // Footer
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "italic");
        pdf.text("Bu rapor Insura AI sistemi tarafindan otomatik olarak olusturulmustur.", 105, 280, { align: "center" });
        
        pdf.save(`insura-rapor-${reportData.reportId || Date.now()}.pdf`);
      }
    } catch (error) {
      console.error("PDF export failed:", error);
      alert("PDF oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setExporting(null);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleCopyToClipboard = async () => {
    try {
      const markdown = generateMarkdownReport(reportData);
      await navigator.clipboard.writeText(markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Copy failed:", error);
      alert("Kopyalama başarısız. Lütfen tekrar deneyin.");
    }
  };

  return (
    <div className="flex gap-2 flex-wrap">
      <button
        onClick={handleCopyToClipboard}
        className="btn btn-outline flex items-center gap-2 text-sm"
        title="Raporu panoya kopyala"
      >
        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
        {copied ? "Kopyalandı!" : "Kopyala"}
      </button>

      <button
        onClick={handlePrint}
        className="btn btn-outline flex items-center gap-2 text-sm"
        title="Raporu yazdır"
      >
        <Printer className="w-4 h-4" />
        Yazdır
      </button>

      <button
        onClick={handleExportPDF}
        disabled={exporting === "pdf"}
        className="btn btn-primary flex items-center gap-2 text-sm"
      >
        <Download className="w-4 h-4" />
        {exporting === "pdf" ? "Oluşturuluyor..." : "PDF İndir"}
      </button>
    </div>
  );
}
